import { v4 as uuid } from 'uuid';

import {
  EXTERNAL_DECISION_STATUS,
  TASK_PRIORITY,
  TASK_STATUS,
  TASK_TYPE,
  TChangePlan,
  TChangeV2,
  TPlanTask,
  TTaskType,
  TWorkPackage,
} from '../models';
import { getDayOffset } from './format';

// -- Work packages -----------------------------------------------------------
// The demo work packages (prompt Part 7). Tasks are grouped into these controlled
// sections rather than dragged between Kanban columns.
const WORK_PACKAGE_DEFS: { name: string; responsibleFunction: string }[] = [
  { name: 'Supplier qualification', responsibleFunction: 'Supplier quality' },
  { name: 'Product documentation', responsibleFunction: 'Engineering or R&D' },
  { name: 'Risk and biological evaluation', responsibleFunction: 'Engineering or R&D' },
  { name: 'Verification', responsibleFunction: 'Engineering or R&D' },
  { name: 'Regulatory readiness', responsibleFunction: 'Regulatory affairs' },
  { name: 'Production cutover', responsibleFunction: 'Production or operations' },
  { name: 'General activities', responsibleFunction: 'Change manager' },
];

const workPackageId = (name: string): string => `wp-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

export const buildWorkPackages = (): TWorkPackage[] =>
  WORK_PACKAGE_DEFS.map((def, index) => ({
    id: workPackageId(def.name),
    name: def.name,
    responsibleFunction: def.responsibleFunction,
    order: index,
  }));

// Routes a task type into its work package.
export const workPackageForType = (type: TTaskType): string => {
  switch (type) {
    case TASK_TYPE.SUPPLIER_QUALIFICATION:
      return workPackageId('Supplier qualification');
    case TASK_TYPE.DOCUMENT_REVISION:
      return workPackageId('Product documentation');
    case TASK_TYPE.RISK_MANAGEMENT:
    case TASK_TYPE.BIOLOGICAL_EVALUATION:
      return workPackageId('Risk and biological evaluation');
    case TASK_TYPE.VERIFICATION_VALIDATION:
      return workPackageId('Verification');
    case TASK_TYPE.REGULATORY_ASSESSMENT:
    case TASK_TYPE.EXTERNAL_DECISION:
    case TASK_TYPE.TRAINING_REQUIREMENT:
      return workPackageId('Regulatory readiness');
    case TASK_TYPE.PRODUCTION_CUTOVER:
      return workPackageId('Production cutover');
    default:
      return workPackageId('General activities');
  }
};

const responsibleFunctionForType = (type: TTaskType): string => {
  const wpId = workPackageForType(type);
  return WORK_PACKAGE_DEFS.find((def) => workPackageId(def.name) === wpId)?.responsibleFunction ?? 'Change manager';
};

const addDays = (baseDate: Date, days: number): string => {
  const result = new Date(
    Date.UTC(baseDate.getUTCFullYear(), baseDate.getUTCMonth(), baseDate.getUTCDate() + Math.trunc(days))
  );
  return result.toISOString();
};

// Builds a complete draft plan from the immutable assessment-template snapshot.
// Static task details come from configuration; only ids and calendar dates are
// generated. Injecting `currentDate` makes the date calculation testable.
export const buildPlanFromAssessment = (change: TChangeV2, currentDate = new Date()): TChangePlan => {
  const assessment = change.assessment;
  const activities = assessment?.snapshot.preliminaryActivities ?? [];
  const tasks: TPlanTask[] = [];
  const taskIds = new Map(activities.map((activity) => [activity.id, `task-${uuid()}`]));

  const makeTask = (partial: Partial<TPlanTask> & Pick<TPlanTask, 'title' | 'taskType'>): TPlanTask => ({
    id: `task-${uuid()}`,
    description: undefined,
    workPackageId: workPackageForType(partial.taskType),
    responsibleFunction: responsibleFunctionForType(partial.taskType),
    contributors: [],
    status: TASK_STATUS.NOT_STARTED,
    priority: TASK_PRIORITY.NORMAL,
    dependencies: [],
    required: true,
    origin: 'Assessment',
    ...partial,
  });

  activities.forEach((activity) => {
    tasks.push(
      makeTask({
        id: taskIds.get(activity.id),
        title: activity.title,
        description: activity.description,
        taskType: activity.taskType,
        responsibleFunction: activity.responsibleFunction,
        owner: activity.owner,
        priority: activity.priority,
        dueDate: addDays(currentDate, activity.dueInDays),
        dependencies: (activity.dependencies ?? []).flatMap((activityId) => {
          const taskId = taskIds.get(activityId);
          return taskId ? [taskId] : [];
        }),
        completionCriteria: activity.completionCriteria,
        linkedObject: activity.linkedObject,
        documents: activity.documents,
        implementationProposal: activity.implementationProposal,
        origin: 'Assessment',
        sourceActivityId: activity.id,
      })
    );
  });

  const calculatedTargetDate = tasks
    .map((task) => task.dueDate)
    .filter((date): date is string => Boolean(date))
    .sort()
    .at(-1);

  return {
    workPackages: buildWorkPackages(),
    tasks,
    // The plan belongs to the same connected Change record. Its target is the
    // date agreed during request/triage, not the last template-relative task.
    targetDate: change.targetDate ?? calculatedTargetDate,
    reportedImpacts: [],
    reassessmentRequired: false,
  };
};

// -- Derived selectors --------------------------------------------------------

export const activeTasks = (plan: TChangePlan): TPlanTask[] => plan.tasks;

export const isTaskComplete = (task: TPlanTask): boolean => task.status === TASK_STATUS.COMPLETE;

const DUE_SOON_DAYS = 7;

export const isTaskOverdue = (task: TPlanTask): boolean => {
  if (task.status === TASK_STATUS.COMPLETE) return false;
  const offset = getDayOffset(task.dueDate);
  return offset !== undefined && offset < 0;
};

export const isTaskDueSoon = (task: TPlanTask): boolean => {
  if (task.status === TASK_STATUS.COMPLETE) return false;
  const offset = getDayOffset(task.dueDate);
  return offset !== undefined && offset >= 0 && offset <= DUE_SOON_DAYS;
};

export const isAwaitingExternal = (task: TPlanTask): boolean =>
  Boolean(task.external) && task.externalStatus !== EXTERNAL_DECISION_STATUS.DECISION_RECEIVED;

export type TPlanSummary = {
  tasks: number;
  completed: number;
  unassigned: number;
  missingDueDate: number;
  targetDate?: string;
};

export const getPlanSummary = (plan: TChangePlan): TPlanSummary => {
  const active = activeTasks(plan);
  return {
    tasks: active.length,
    completed: active.filter(isTaskComplete).length,
    unassigned: active.filter((task) => task.required && !task.owner).length,
    missingDueDate: active.filter((task) => task.required && !task.dueDate).length,
    targetDate: plan.targetDate,
  };
};

export type TAttentionSummary = {
  overdue: number;
  dueSoon: number;
  awaitingExternal: number;
};

export const getAttentionSummary = (plan: TChangePlan): TAttentionSummary => {
  const active = activeTasks(plan);
  return {
    overdue: active.filter(isTaskOverdue).length,
    dueSoon: active.filter(isTaskDueSoon).length,
    awaitingExternal: active.filter(isAwaitingExternal).length,
  };
};

// -- Plan readiness (Screen 7) -----------------------------------------------
// A plan is ready when every required task has an owner and a due date, required
// completion criteria are defined and required external activities are represented.
export const getPlanReadiness = (plan: TChangePlan): { ready: boolean; issues: string[] } => {
  const active = activeTasks(plan);
  const required = active.filter((task) => task.required);
  const issues: string[] = [];

  const missingOwner = required.filter((task) => !task.owner).length;
  if (missingOwner > 0) issues.push(`${missingOwner} required task${missingOwner === 1 ? '' : 's'} without an owner`);

  const missingDue = required.filter((task) => !task.dueDate).length;
  if (missingDue > 0) issues.push(`${missingDue} required task${missingDue === 1 ? '' : 's'} without a due date`);

  const missingCriteria = required.filter((task) => !task.completionCriteria?.trim()).length;
  if (missingCriteria > 0)
    issues.push(`${missingCriteria} required task${missingCriteria === 1 ? '' : 's'} without completion criteria`);

  return { ready: issues.length === 0, issues };
};

// -- Release-readiness eligibility (Screen 8) --------------------------------
const DOCUMENT_TYPES: TTaskType[] = [TASK_TYPE.DOCUMENT_REVISION];
const VNV_TYPES: TTaskType[] = [TASK_TYPE.VERIFICATION_VALIDATION];
const REGULATORY_TYPES: TTaskType[] = [TASK_TYPE.REGULATORY_ASSESSMENT];

export const getReleaseReadiness = (change: TChangeV2): { eligible: boolean; blockers: string[] } => {
  const plan = change.plan;
  if (!plan) return { eligible: false, blockers: ['No plan has been created yet.'] };

  const active = activeTasks(plan);
  const blockers: string[] = [];

  const mandatoryOpen = active.filter((task) => task.required && !task.external && !isTaskComplete(task));
  if (mandatoryOpen.length > 0)
    blockers.push(`${mandatoryOpen.length} mandatory task${mandatoryOpen.length === 1 ? '' : 's'} not complete`);

  const docsOpen = active.filter((task) => DOCUMENT_TYPES.includes(task.taskType) && !isTaskComplete(task));
  if (docsOpen.length > 0) blockers.push('Required documents are not yet approved');

  const vnvOpen = active.filter((task) => VNV_TYPES.includes(task.taskType) && !isTaskComplete(task));
  if (vnvOpen.length > 0) blockers.push('Verification and validation are not complete');

  const regOpen = active.filter((task) => REGULATORY_TYPES.includes(task.taskType) && !isTaskComplete(task));
  if (regOpen.length > 0) blockers.push('Regulatory assessments are not complete');

  const externalOpen = active.filter((task) => task.releaseBlocking && isAwaitingExternal(task));
  if (externalOpen.length > 0) blockers.push('An external blocking decision is unresolved');

  if (plan.reassessmentRequired) blockers.push('A material reassessment is open');

  return { eligible: blockers.length === 0, blockers };
};

// True when completing this task requires confirmation against its criteria.
export const taskNeedsCompletionDialog = (task: TPlanTask): boolean => Boolean(task.completionCriteria?.trim());

export const tasksInWorkPackage = (plan: TChangePlan, workPackageId: string): TPlanTask[] =>
  plan.tasks.filter((task) => task.workPackageId === workPackageId);
